import { useRef, useState, type KeyboardEvent } from 'react';
import { useMapContext, type MeasurementEntity } from '../../map/MapContext';
import { measurementProperties, PRESET_COLORS } from '../../map/measurement';
import { DetailPanel } from '../DetailPanel/DetailPanel';
import { CircleToolIcon, LineToolIcon, PointToolIcon, ZoneToolIcon } from '../icons/Icons';

const TYPE_ICON: Record<MeasurementEntity['type'], (props: { size?: number }) => JSX.Element> = {
  point: PointToolIcon,
  line: LineToolIcon,
  zone: ZoneToolIcon,
  circle: CircleToolIcon,
};

interface MeasurementDetailPanelProps {
  measurement: MeasurementEntity;
  onClose: () => void;
}

/**
 * Detail panel for a clicked measurement (Point/Line/Zone/Circle) — same
 * DetailPanel shell as PhotoDetailPanel, with measurement-specific
 * Properties (Surface/Horizontal Area, Length, or X/Y/Z — see
 * src/map/measurement.ts's measurementProperties()), a decorative "Access"
 * row, and a real "More" menu (Rename/Set colour/Duplicate/Delete working;
 * Set icon/Export/Move to folder/Publish to content stubbed — this app has
 * no backend/file infrastructure yet for those, per the plan's scoping).
 */
export function MeasurementDetailPanel({ measurement, onClose }: MeasurementDetailPanelProps) {
  const {
    map,
    setMeasurementVisible,
    updateMeasurementDescription,
    renameMeasurement,
    duplicateMeasurement,
    setMeasurementColor,
    removeMeasurement,
    folders,
    moveMeasurementToFolder,
  } = useMapContext();
  const [description, setDescription] = useState(measurement.description);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(measurement.name);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [folderListOpen, setFolderListOpen] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const Icon = TYPE_ICON[measurement.type];
  const properties = measurementProperties(measurement, map.getView().getProjection());

  function onDescriptionChange(value: string) {
    setDescription(value);
    updateMeasurementDescription(measurement.id, value);
  }

  function startRename() {
    setNameDraft(measurement.name);
    setRenaming(true);
    // Focus once the input has actually mounted.
    requestAnimationFrame(() => renameInputRef.current?.select());
  }

  function commitRename() {
    renameMeasurement(measurement.id, nameDraft);
    setRenaming(false);
  }

  function onRenameKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') commitRename();
    if (e.key === 'Escape') setRenaming(false);
  }

  function onDelete() {
    removeMeasurement(measurement.id);
    // removeMeasurement already clears selection if this was selected, but
    // call onClose too so the panel unmounts immediately rather than
    // waiting on the next MapView render.
    onClose();
  }

  return (
    <DetailPanel
      icon={<Icon size={15} />}
      title={measurement.name}
      titleContent={
        renaming ? (
          <input
            ref={renameInputRef}
            className="detail-panel-rename-input"
            value={nameDraft}
            autoFocus
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={onRenameKeyDown}
          />
        ) : undefined
      }
      onClose={onClose}
      visible={measurement.visible}
      onToggleVisible={() => setMeasurementVisible(measurement.id, !measurement.visible)}
      moreMenu={(close) => (
        <div className="detail-more-menu" role="menu">
          <button
            type="button"
            className="detail-more-item"
            role="menuitem"
            onClick={() => {
              startRename();
              close();
            }}
          >
            Rename
          </button>
          <button
            type="button"
            className="detail-more-item"
            role="menuitem"
            aria-haspopup="true"
            aria-expanded={colorPickerOpen}
            onClick={(e) => {
              e.stopPropagation();
              setColorPickerOpen((v) => !v);
            }}
          >
            Set colour <span className="detail-more-chevron">›</span>
          </button>
          {colorPickerOpen && (
            <div className="detail-more-swatches">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`detail-more-swatch${(measurement.color ?? PRESET_COLORS[0]) === c ? ' active' : ''}`}
                  style={{ background: c }}
                  aria-label={`Set colour ${c}`}
                  onClick={() => {
                    setMeasurementColor(measurement.id, c);
                    setColorPickerOpen(false);
                    close();
                  }}
                />
              ))}
            </div>
          )}
          <button type="button" className="detail-more-item" role="menuitem" disabled title="Not available yet">
            Set icon <span className="detail-more-chevron">›</span>
          </button>
          <button type="button" className="detail-more-item" role="menuitem" disabled title="Not available yet">
            Export
          </button>
          <button
            type="button"
            className="detail-more-item"
            role="menuitem"
            onClick={() => {
              duplicateMeasurement(measurement.id);
              close();
            }}
          >
            Duplicate
          </button>
          <button
            type="button"
            className="detail-more-item"
            role="menuitem"
            aria-haspopup="true"
            aria-expanded={folderListOpen}
            onClick={(e) => {
              e.stopPropagation();
              setFolderListOpen((v) => !v);
            }}
          >
            Move to folder <span className="detail-more-chevron">›</span>
          </button>
          {folderListOpen && (
            <div className="detail-more-folders" role="group" aria-label="Folders">
              {[{ folder_id: null as string | null, name: 'No folder' }, ...folders].map((f) => {
                const current = (measurement.folderId ?? null) === f.folder_id;
                return (
                  <button
                    key={f.folder_id ?? 'none'}
                    type="button"
                    className={`detail-more-item detail-more-folder${current ? ' active' : ''}`}
                    role="menuitemradio"
                    aria-checked={current}
                    onClick={() => {
                      if (!current) moveMeasurementToFolder(measurement.id, f.folder_id);
                      setFolderListOpen(false);
                      close();
                    }}
                  >
                    {f.name}
                  </button>
                );
              })}
            </div>
          )}
          <button type="button" className="detail-more-item" role="menuitem" disabled title="Not available yet">
            Publish to content
          </button>
          <div className="detail-more-divider" />
          <button
            type="button"
            className="detail-more-item detail-more-danger"
            role="menuitem"
            onClick={() => {
              onDelete();
              close();
            }}
          >
            Delete
          </button>
        </div>
      )}
      meta={
        <div>
          Created by {measurement.createdBy} · {new Date(measurement.createdAt).toLocaleString()}
        </div>
      }
      access={
        <div className="detail-panel-access">
          <span className="detail-panel-avatar">{measurement.createdBy}</span>
          <span>Only you can see this — sharing isn't available yet.</span>
        </div>
      }
      properties={properties.map((p) => ({ label: p.label, value: p.value }))}
      description={description}
      onDescriptionChange={onDescriptionChange}
    />
  );
}
